import { useEffect, useState } from 'react';

import {
  Button,
  Card,
  CircularProgress,
  Divider,
  Grid,
  TextField,
  Typography,
  useTheme,
} from '@material-ui/core';
import EmojiEventsOutlinedIcon from '@material-ui/icons/EmojiEventsOutlined';
import HighlightOffIcon from '@material-ui/icons/HighlightOff';
import { Autocomplete } from '@material-ui/lab';
import { FormikProvider, useFormik } from 'formik';
import { useSnackbar } from 'notistack';
import { useModal } from 'react-modal-hook';
import * as yup from 'yup';

import { useAppDispatch, useAppSelector } from 'store';
import {
  fetchFunnelStagesSelect,
  fetchFunnelStageStatus,
  funnelsSelector,
  FunnelStage,
  FunnelStageStatus,
  moveOpportunities,
  MoveOpportunitiesPayload,
  OpportunityStageStatus,
} from 'store/reducers/funnels';
import emptyStringToNull from 'utils/emptyStringToNull';

import MarkOpportunityAsLossConfirm from './MarkOpportunityAsLossConfirm';
import MarkOpportunityAsWonConfirm from './MarkOpportunityAsWonConfirm';

const validationSchema = yup.object({
  stage: yup.object().nullable().required('O estágio é obrigatório'),
  status: yup.object().nullable(),
});

type FormValues = {
  stage: FunnelStage | null;
  stageStatus: FunnelStageStatus | null;
};

interface MoveOpportunityFunnelFormProps {
  funnelId: string;
  opportunityId: string;
  currentStageStatus: OpportunityStageStatus | undefined;
  stage: FunnelStage | undefined;
  onCloseDrawer: () => void;
  onSuccessMove?: () => void;
  onPendingChange?: (hasPending: boolean) => void;
}

function MoveOpportunityFunnelForm(props: MoveOpportunityFunnelFormProps) {
  const {
    funnelId,
    opportunityId,
    currentStageStatus,
    stage,
    onCloseDrawer,
    onSuccessMove,
    onPendingChange,
  } = props;

  const theme = useTheme();
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const [showActions, setShowActions] = useState(false);

  const { stageStatus, funnelStages, loadingFunnelStages, loadingStageStatus } =
    useAppSelector(funnelsSelector);

  const formik = useFormik<FormValues>({
    enableReinitialize: true,
    initialValues: {
      stage: stage || null,
      stageStatus: (currentStageStatus as FunnelStageStatus) || null,
    },
    validationSchema,
    onSubmit: async (values: FormValues) => {
      const toStageId = values.stage?.id;

      if (!toStageId) {
        return;
      }

      const payload: MoveOpportunitiesPayload = {
        funnelId,
        opportunityIds: [opportunityId],
        toStageId,
        statusId: emptyStringToNull(values.stageStatus?.id),
      };

      await dispatch(moveOpportunities(payload))
        .unwrap()
        .then(() => {
          enqueueSnackbar('Alteração realizada com sucesso.');
          setShowActions(false);

          if (onPendingChange) onPendingChange(false);

          if (onSuccessMove) onSuccessMove();
        })
        .catch(error => {
          if (error.response) enqueueSnackbar(error.response.data.message);
        });
    },
  });

  const selectedStageId = formik.values.stage?.id;

  const stageError = formik.touched.stage && formik.errors.stage;

  const handleCancel = () => {
    setShowActions(false);

    if (onPendingChange) onPendingChange(false);
  };

  const handleFieldFocus = () => {
    setShowActions(true);

    if (onPendingChange) onPendingChange(true);
  };

  const [showMarkOpportunityAsWonConfirm, hideMarkOpportunityAsWonConfirm] =
    useModal(
      ({ in: open }) => {
        return (
          <MarkOpportunityAsWonConfirm
            funnelId={funnelId}
            opportunityIds={[opportunityId]}
            open={open}
            onClose={hideMarkOpportunityAsWonConfirm}
            onSuccess={onCloseDrawer}
          />
        );
      },
      [funnelId, opportunityId],
    );

  const [showMarkOpportunityAsLossConfirm, hideMarkOpportunityAsLossConfirm] =
    useModal(
      ({ in: open }) => {
        return (
          <MarkOpportunityAsLossConfirm
            funnelId={funnelId}
            opportunityIds={[opportunityId]}
            open={open}
            onClose={hideMarkOpportunityAsLossConfirm}
            onSuccess={onCloseDrawer}
          />
        );
      },
      [funnelId, opportunityId],
    );

  useEffect(() => {
    if (selectedStageId) {
      dispatch(fetchFunnelStageStatus({ funnelId, stageId: selectedStageId }));
    }
  }, [dispatch, funnelId, selectedStageId]);

  useEffect(() => {
    if (funnelId) {
      dispatch(fetchFunnelStagesSelect({ funnelId }));
    }
  }, [dispatch, funnelId]);

  return (
    <FormikProvider value={formik}>
      <Card variant="outlined" style={{ padding: 24 }}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            {/* ESTÁGIO */}
            <Autocomplete
              id="stage"
              options={funnelStages || []}
              loading={loadingFunnelStages}
              getOptionLabel={option => option.name}
              getOptionSelected={(option, value) => option.id === value.id}
              value={formik.values.stage}
              onFocus={handleFieldFocus}
              onChange={(event, newValue, reason) => {
                if (newValue) {
                  formik.setFieldValue('stage', newValue);
                  formik.setFieldValue('stageStatus', null);
                }

                if (reason === 'clear') {
                  formik.setFieldValue('stage', null);
                }
              }}
              noOptionsText={
                <Typography variant="body2">
                  Nenhuma etapa encontrada
                </Typography>
              }
              clearText="Limpar"
              closeText="Fechar"
              openText="Abrir"
              renderInput={params => (
                <TextField
                  {...params}
                  label="Etapa do funil"
                  variant="outlined"
                  placeholder="Pesquise"
                  error={Boolean(stageError)}
                  helperText={stageError}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingFunnelStages ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            {/* STATUS */}
            <Autocomplete
              id="stageStatus"
              options={stageStatus || []}
              loading={loadingStageStatus}
              disabled={!formik.values.stage}
              onFocus={handleFieldFocus}
              getOptionLabel={option => option.name}
              getOptionSelected={(option, value) => option.id === value.id}
              value={formik.values.stageStatus}
              onChange={(_, newValue, reason) => {
                if (newValue) formik.setFieldValue('stageStatus', newValue);

                if (reason === 'clear') {
                  formik.setFieldValue('stageStatus', null);
                }
              }}
              noOptionsText={
                <Typography variant="body2">
                  Nenhum status encontrado
                </Typography>
              }
              clearText="Limpar"
              closeText="Fechar"
              openText="Abrir"
              renderInput={params => (
                <TextField
                  {...params}
                  label="Status"
                  variant="outlined"
                  placeholder="Pesquise"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingStageStatus ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Grid>

          {showActions ? (
            <Grid item xs={12}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Button onClick={handleCancel} variant="outlined" fullWidth>
                    cancelar
                  </Button>
                </Grid>

                <Grid item xs={6}>
                  <Button
                    color="primary"
                    variant="contained"
                    fullWidth
                    disabled={formik.isSubmitting}
                    onClick={formik.submitForm}
                  >
                    Salvar
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          ) : (
            <>
              <Grid item xs={12}>
                <Divider />
              </Grid>

              <Grid item xs={12}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Button
                      style={{
                        color: theme.palette.success.main,
                        borderColor: theme.palette.success.main,
                      }}
                      onClick={showMarkOpportunityAsWonConfirm}
                      startIcon={<EmojiEventsOutlinedIcon />}
                      variant="outlined"
                      fullWidth
                    >
                      Ganho
                    </Button>
                  </Grid>

                  <Grid item xs={6}>
                    <Button
                      style={{
                        color: theme.palette.error.main,
                        borderColor: theme.palette.error.main,
                      }}
                      onClick={showMarkOpportunityAsLossConfirm}
                      startIcon={<HighlightOffIcon />}
                      variant="outlined"
                      fullWidth
                      color="primary"
                    >
                      Perda
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </>
          )}
        </Grid>
      </Card>
    </FormikProvider>
  );
}

export default MoveOpportunityFunnelForm;
